import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { createPageUrl } from '@/utils';
import { notify } from '@/components/ui/notify';
import { fetchAllCompetitorMarketData } from '@/lib/competitorsApi';
import {
  ColumnHeaderHelp,
  CompetitorProximityBar,
  TrajectoryCell,
  TruncatedCell,
} from '@/components/competitors/competitorUi';
import {
  ALL_COUNTRIES_VALUE,
  allSummaryCountsZero,
  competitorHasTargetSignals,
  computeSummaryCards,
  formatRelativeDate,
  impactBadgeClass,
  isCompetitorMarketEmpty,
  marketSummaryBodyText,
} from '@/lib/competitorDisplay';
import { translate, useTranslation } from '@/components/utils/translations';

// Rendered via t('mktTrajectoryTooltip') — kept as a key so it is translated at render time.
const TRAJECTORY_COLUMN_TOOLTIP_KEY = 'mktTrajectoryTooltip';

/** Display-only translation for impact values; the raw value still drives impactBadgeClass. */
const IMPACT_LABEL_KEYS = {
  Favourable: 'mktFavourable',
  Unfavourable: 'mktUnfavourable',
  Mixed: 'mktMixed',
  'Context only': 'mktContextOnly',
};

function impactLabel(t, impact) {
  const key = Object.prototype.hasOwnProperty.call(IMPACT_LABEL_KEYS, impact) ? IMPACT_LABEL_KEYS[impact] : null;
  return key ? t(key) : impact;
}

/** Same count rule as pluralizeCount (Number(n) || 0, singular only at 1), translated. */
function pluralizeCountT(t, n, oneKey, manyKey) {
  const count = Number(n) || 0;
  return t(count === 1 ? oneKey : manyKey, { n: count });
}

function SummaryCards({ summary }) {
  const { t } = useTranslation();
  if (allSummaryCountsZero(summary)) {
    return (
      <Card className="border-0 shadow-md">
        <CardContent className="p-6 text-center text-sm text-[#5C6B7A]">
          {t('mktNoCompetitorEvidence')}
        </CardContent>
      </Card>
    );
  }

  const cards = [
    { label: 'Total competitor signals', labelKey: 'mktTotalCompetitorSignals', value: summary.totalSignals },
    ...(summary.newSignals > 0
      ? [{ label: 'New signals (last 90d)', labelKey: 'mktNewSignals90d', value: summary.newSignals }]
      : []),
    { label: 'High-proximity accounts', labelKey: 'mktHighProximityAccounts', value: summary.highProximity },
    { label: 'Personas with competitor links', labelKey: 'mktPersonasWithCompetitorLinks', value: summary.personasWithLinks },
    { label: 'Favourable displacement opportunities', labelKey: 'mktFavourableDisplacementOpps', value: summary.favourable },
    { label: 'Unfavourable competitor blockers', labelKey: 'mktUnfavourableBlockers', value: summary.unfavourable },
    {
      label: 'Last refreshed',
      labelKey: 'mktLastRefreshed',
      value: summary.lastRefreshed ? formatRelativeDate(summary.lastRefreshed) : '—',
    },
  ];

  return (
    <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
      {cards.map((c) => (
        <Card key={c.label} className="border-0 shadow-sm">
          <CardContent className="p-3">
            <p className="text-[10px] uppercase tracking-wide text-[#5C6B7A] mb-1">{c.labelKey ? t(c.labelKey) : c.label}</p>
            <p className="text-lg font-semibold text-[#0A1A2F]">{c.value}</p>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

function DataTable({ columns, rows, onRowClick, emptyMessage }) {
  if (!rows?.length) return null;
  return (
    <div className="overflow-x-auto rounded-lg border border-gray-200">
      <table className="w-full text-sm">
        <thead className="bg-gray-50 text-left text-xs text-[#5C6B7A]">
          <tr>
            {columns.map((col) => (
              <th key={col.key} className="px-3 py-2 font-medium whitespace-nowrap">
                <ColumnHeaderHelp label={col.label} tooltip={col.tooltip} />
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {rows.map((row, idx) => (
            <tr
              key={row._key || idx}
              className={onRowClick ? 'cursor-pointer hover:bg-gray-50' : ''}
              onClick={() => onRowClick?.(row)}
            >
              {columns.map((col) => (
                <td key={col.key} className="px-3 py-2 align-top text-[#0A1A2F]">
                  {col.render(row)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
      {rows.length === 0 && emptyMessage ? (
        <p className="p-4 text-sm text-[#5C6B7A] text-center">{emptyMessage}</p>
      ) : null}
    </div>
  );
}

export default function CompetitorsTab({ tenantId, countryFilter = ALL_COUNTRIES_VALUE }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    if (!tenantId) return;
    setLoading(true);
    setError(null);
    try {
      const result = await fetchAllCompetitorMarketData(tenantId, countryFilter);
      setData(result);
    } catch (e) {
      console.error('Competitors tab load failed:', e);
      setError(e);
      notify({
        // translate() (not the hook's t) keeps this callback's dependency list unchanged.
        title: translate('error'),
        description: translate('mktCouldNotLoadCompetitorData'),
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  }, [tenantId, countryFilter]);

  useEffect(() => {
    load();
  }, [load]);

  const summary = useMemo(
    () => (data ? computeSummaryCards(data) : null),
    [data],
  );

  const goToSignals = (competitorName, tab = 'momentum') => {
    const params = new URLSearchParams({ tab });
    if (competitorName) params.set('competitor', competitorName);
    navigate(createPageUrl(`Signals?${params.toString()}`));
  };

  const handleCompetitorNavigate = (row) => {
    if (competitorHasTargetSignals(row)) {
      goToSignals(row.competitor_name, 'momentum');
    } else {
      goToSignals(row.competitor_name, 'market');
    }
  };

  const competitorRows = data?.competitorView?.rows || [];
  const accountRows = data?.accountBarometer?.rows || [];
  const personaRows = data?.personaBarometer?.rows || [];

  const showNewSignalsColumn = competitorRows.some(
    (r) => (Number(r.new_signals_this_window) || 0) > 0,
  );

  const marketTrackerColumns = useMemo(() => {
    const cols = [
      { key: 'market', label: t('market'), render: () => (countryFilter === ALL_COUNTRIES_VALUE ? t('all') : countryFilter) },
      {
        key: 'name',
        label: t('mktCompetitor'),
        render: (r) => <span className="font-medium">{r.competitor_name}</span>,
      },
      {
        key: 'accounts',
        label: t('mktAccountsAffected'),
        tooltip: t('mktAccountsAffectedTip'),
        render: (r) => r.accounts_affected,
      },
      {
        key: 'personas',
        label: t('mktPersonasAffected'),
        tooltip: t('mktPersonasAffectedTip'),
        render: (r) => r.personas_affected,
      },
      {
        key: 'market_events',
        label: t('mktMarketEvents'),
        tooltip: t('mktMarketEventsTip'),
        render: (r) => r.market_updates_count ?? 0,
      },
    ];
    if (showNewSignalsColumn) {
      cols.push({
        key: 'new',
        label: t('mktNewSignals90d'),
        tooltip: t('mktNewSignals90dTip'),
        render: (r) => r.new_signals_this_window,
      });
    }
    cols.push(
      {
        key: 'impact',
        label: t('mktNetImpact'),
        tooltip: t('mktNetImpactTip'),
        render: (r) => (
          <Badge className={`text-xs border ${impactBadgeClass(r.net_impact)}`}>{impactLabel(t, r.net_impact)}</Badge>
        ),
      },
      {
        key: 'themes',
        label: t('mktMainThemes'),
        tooltip: t('mktMainThemesTip'),
        render: (r) => <TruncatedCell text={(r.main_themes || []).join(', ')} />,
      },
      {
        key: 'latest',
        label: t('mktLatestUpdate'),
        tooltip: t('mktLatestUpdateTip'),
        render: (r) => <TruncatedCell text={r.latest_evidence} />,
      },
    );
    return cols;
  }, [countryFilter, showNewSignalsColumn, t]);

  const accountBarometerColumns = useMemo(
    () => [
      { key: 'account', label: t('account'), render: (r) => r.account_name },
      {
        key: 'competitor',
        label: t('mktTopCompetitor'),
        tooltip: t('mktTopCompetitorAccountTip'),
        render: (r) => r.top_competitor || '—',
      },
      {
        key: 'score',
        label: t('mktBarometerScore'),
        tooltip: t('mktBarometerScoreAccountTip'),
        render: (r) => <CompetitorProximityBar score={r.proximity_score} compact />,
      },
      {
        key: 'impact',
        label: t('impactSuffix'),
        tooltip: t('mktImpactAccountTip'),
        render: (r) => (
          <Badge className={`text-xs border ${impactBadgeClass(r.impact)}`}>{impactLabel(t, r.impact)}</Badge>
        ),
      },
      {
        key: 'trajectory',
        label: t('mktTrajectory24m'),
        tooltip: t(TRAJECTORY_COLUMN_TOOLTIP_KEY),
        render: (r) => <TrajectoryCell series={r.score_trajectory} />,
      },
      {
        key: 'date',
        label: t('mktLatestSignal'),
        tooltip: t('mktLatestSignalAccountTip'),
        render: (r) => formatRelativeDate(r.latest_signal_date),
      },
      {
        key: 'evidence',
        label: t('mktStrongestEvidence'),
        tooltip: t('mktStrongestEvidenceAccountTip'),
        render: (r) => <TruncatedCell text={r.strongest_evidence} />,
      },
      {
        key: 'rec',
        label: t('mktRecommendation'),
        tooltip: t('mktRecommendationTip'),
        render: (r) => (
          <Link
            to={createPageUrl(`AccountFinal?id=${encodeURIComponent(r.account_id)}&tab=sales-map`)}
            className="text-xs text-[#00C2C2] hover:underline"
            onClick={(e) => e.stopPropagation()}
          >
            {t('mktOpenSalesMap')}
          </Link>
        ),
      },
    ],
    [t],
  );

  const personaBarometerColumns = useMemo(
    () => [
      { key: 'person', label: t('mktPersona'), render: (r) => r.full_name },
      {
        key: 'account',
        label: t('account'),
        render: (r) => (
          <div>
            <div>{r.account_name || '—'}</div>
          </div>
        ),
      },
      {
        key: 'competitor',
        label: t('mktTopCompetitor'),
        tooltip: t('mktTopCompetitorPersonaTip'),
        render: (r) => r.top_competitor || '—',
      },
      {
        key: 'score',
        label: t('mktBarometerScore'),
        tooltip: t('mktBarometerScorePersonaTip'),
        render: (r) => <CompetitorProximityBar score={r.proximity_score} compact />,
      },
      {
        key: 'impact',
        label: t('impactSuffix'),
        tooltip: t('mktImpactPersonaTip'),
        render: (r) => (
          <Badge className={`text-xs border ${impactBadgeClass(r.impact)}`}>{impactLabel(t, r.impact)}</Badge>
        ),
      },
      {
        key: 'trajectory',
        label: t('mktTrajectory24m'),
        tooltip: t(TRAJECTORY_COLUMN_TOOLTIP_KEY),
        render: (r) => <TrajectoryCell series={r.score_trajectory} />,
      },
      {
        key: 'date',
        label: t('mktLatestSignal'),
        tooltip: t('mktLatestSignalPersonaTip'),
        render: (r) => formatRelativeDate(r.latest_signal_date),
      },
      {
        key: 'evidence',
        label: t('mktStrongestEvidence'),
        tooltip: t('mktStrongestEvidencePersonaTip'),
        render: (r) => <TruncatedCell text={r.strongest_evidence} />,
      },
    ],
    [t],
  );

  const tabLevelEmpty = !loading && data && isCompetitorMarketEmpty(data, summary);
  const isRefreshing = loading && Boolean(data);
  const marketSummary = data?.marketSummary;
  const marketSummaryText = marketSummaryBodyText(marketSummary);
  const showMarketSummaryCard =
    Boolean(marketSummary) &&
    !tabLevelEmpty &&
    (!isRefreshing || Boolean(String(marketSummary?.summary || '').trim()) || marketSummary?.unavailable === true);

  if (loading && !data) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-10 w-full" />
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {Array.from({ length: 7 }).map((_, i) => (
            <Skeleton key={i} className="h-20" />
          ))}
        </div>
        <Skeleton className="h-48 w-full" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  return (
    <div
      className={`space-y-6 ${isRefreshing ? 'opacity-60 pointer-events-none' : ''}`}
      aria-busy={isRefreshing}
    >
      {isRefreshing ? (
        <p className="text-xs text-[#5C6B7A]">{t('refreshing')}</p>
      ) : null}

      {tabLevelEmpty ? (
        <Card className="border-0 shadow-md">
          <CardContent className="p-8 text-center text-sm text-[#5C6B7A]">
            {t('mktNoCompetitorEvidence')}
          </CardContent>
        </Card>
      ) : null}

      {!tabLevelEmpty && summary && !allSummaryCountsZero(summary) ? <SummaryCards summary={summary} /> : null}

      {showMarketSummaryCard ? (
        <Card className="border-0 shadow-md">
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-semibold text-[#0A1A2F]">{t('mktMarketSummary')}</CardTitle>
          </CardHeader>
          <CardContent className="pt-0">
            <p className="text-sm text-[#0A1A2F] leading-relaxed">
              {marketSummaryText}
            </p>
          </CardContent>
        </Card>
      ) : null}

      {competitorRows.length > 0 ? (
        <Card className="border-0 shadow-md">
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-semibold text-[#0A1A2F]">{t('mktMarketTracker')}</CardTitle>
          </CardHeader>
          <CardContent className="pt-0 space-y-2">
            <DataTable
              rows={competitorRows.map((r) => ({ ...r, _key: r.competitor_name }))}
              onRowClick={handleCompetitorNavigate}
              columns={marketTrackerColumns}
            />
          </CardContent>
        </Card>
      ) : null}

      {accountRows.length > 0 ? (
        <Card className="border-0 shadow-md">
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-semibold text-[#0A1A2F]">{t('mktAccountBarometer')}</CardTitle>
          </CardHeader>
          <CardContent className="pt-0">
            <DataTable
              rows={accountRows.map((r) => ({ ...r, _key: r.account_id }))}
              onRowClick={(row) => navigate(createPageUrl(`AccountFinal?id=${encodeURIComponent(row.account_id)}&tab=competitors`))}
              columns={accountBarometerColumns}
            />
          </CardContent>
        </Card>
      ) : null}

      {personaRows.length > 0 ? (
        <Card className="border-0 shadow-md">
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-semibold text-[#0A1A2F]">{t('mktPersonaBarometer')}</CardTitle>
          </CardHeader>
          <CardContent className="pt-0">
            <DataTable
              rows={personaRows.map((r) => ({ ...r, _key: r.person_id }))}
              onRowClick={(row) => navigate(createPageUrl(`PersonProfile?id=${encodeURIComponent(row.person_id)}&tab=competitors`))}
              columns={personaBarometerColumns}
            />
          </CardContent>
        </Card>
      ) : null}

      {competitorRows.length > 0 ? (
        <div className="space-y-4">
          <h3 className="text-base font-semibold text-[#0A1A2F]">{t('mktCompetitorView')}</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {competitorRows.map((row) => (
              <Card
                key={row.competitor_name}
                className="border-0 shadow-md cursor-pointer hover:shadow-lg transition-shadow"
                onClick={() => handleCompetitorNavigate(row)}
              >
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between gap-2">
                    <CardTitle className="text-sm font-semibold">{row.competitor_name}</CardTitle>
                    <Badge className={`text-xs border ${impactBadgeClass(row.net_impact)}`}>{impactLabel(t, row.net_impact)}</Badge>
                  </div>
                </CardHeader>
                <CardContent className="pt-0 space-y-3 text-sm">
                  <p className="text-xs text-[#5C6B7A]">
                    {pluralizeCountT(t, row.accounts_affected, 'mktOneAccount', 'mktNAccounts')} · {pluralizeCountT(t, row.personas_affected, 'mktOnePersona', 'mktNPersonas')}
                    {(Number(row.new_signals_this_window) || 0) > 0
                      ? ` · ${pluralizeCountT(t, row.new_signals_this_window, 'mktOneNewSignal', 'mktNNewSignals')}`
                      : ''}
                    {(Number(row.market_updates_count) || 0) > 0
                      ? ` · ${pluralizeCountT(t, row.market_updates_count, 'mktOneMarketEvent', 'mktNMarketEvents')}`
                      : ''}
                  </p>
                  {(row.strongest_accounts || []).length > 0 ? (
                    <div>
                      <p className="text-xs font-medium text-[#0A1A2F] mb-1">{t('mktStrongestAccounts')}</p>
                      <ul className="text-xs space-y-0.5">
                        {row.strongest_accounts.slice(0, 3).map((a) => (
                          <li key={a.account_id}>
                            <Link
                              to={createPageUrl(`AccountFinal?id=${encodeURIComponent(a.account_id)}&tab=competitors`)}
                              className="text-[#00C2C2] hover:underline"
                              onClick={(e) => e.stopPropagation()}
                            >
                              {a.account_name}
                            </Link>
                            {' '}({a.proximity_score})
                          </li>
                        ))}
                      </ul>
                    </div>
                  ) : null}
                  {(row.strongest_personas || []).length > 0 ? (
                    <div>
                      <p className="text-xs font-medium text-[#0A1A2F] mb-1">{t('mktStrongestPersonas')}</p>
                      <ul className="text-xs space-y-0.5">
                        {row.strongest_personas.slice(0, 3).map((p) => (
                          <li key={p.person_id}>
                            <Link
                              to={createPageUrl(`PersonProfile?id=${encodeURIComponent(p.person_id)}&tab=competitors`)}
                              className="text-[#00C2C2] hover:underline"
                              onClick={(e) => e.stopPropagation()}
                            >
                              {p.full_name}
                            </Link>
                            {' '}({p.proximity_score})
                          </li>
                        ))}
                      </ul>
                    </div>
                  ) : null}
                  {(row.main_themes || []).length > 0 ? (
                    <div className="flex flex-wrap gap-1">
                      {row.main_themes.slice(0, 6).map((theme) => (
                        <span
                          key={theme}
                          className="text-[10px] px-2 py-0.5 rounded-full bg-gray-100 text-[#5C6B7A]"
                        >
                          {theme}
                        </span>
                      ))}
                    </div>
                  ) : null}
                  {row.latest_evidence ? (
                    <TruncatedCell text={row.latest_evidence} />
                  ) : null}
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      ) : null}

      {error && !data ? (
        <div className="text-center py-8">
          <Button variant="outline" onClick={load}>{t('retry')}</Button>
        </div>
      ) : null}
    </div>
  );
}
